/* eslint-disable @typescript-eslint/no-explicit-any */
import { isArray, isNil } from 'lodash-es'
import { v4 as uuidv4 } from 'uuid'

import { HttpError } from './http-error'
import { ApiResponse, AUTH, isSuccessResponse } from './response-code'
import { signOut } from '../lib/auth'

export enum Auth {
  REQUIRED = 'REQUIRED',
  OPTIONAL = 'OPTIONAL',
  NONE = 'NONE',
}

export type TSignInResponse = {
  accessToken: string
  refreshToken: string
  firebaseToken: string
  blocked: boolean // 이용 정지 여부
  certified: boolean // 본인인증 여부
}

export enum ContentType {
  JSON = 'application/json',
  MULTIPART = 'multipart/form-data',
  URLENCODED = 'application/x-www-form-urlencoded',
}

type RequestOption = {
  contentType?: ContentType
  authorization?: string
  tags?: string[]
  cache?: RequestCache
  revalidate?: number
  credentials?: string
  useMock?: boolean
  customHeaders?: Record<string, string>
}

const isServer = typeof window === 'undefined'
const INTERNAL_API_URL = process.env.INTERNAL_API_URL ?? 'http://localhost:8080'
const API_URL = process.env.NEXT_PUBLIC_URL ?? 'http://localhost:3000'

const generateXRequestID = () => {
  return uuidv4()
}

export class ApiClient {
  private baseUrl: string
  private version: string
  private isRefreshing = false
  private failedRequest: Array<{
    resolve: (token: string) => void
  }> = []

  constructor(url: string, version?: string) {
    this.baseUrl = url
    this.version = version ?? 'v1'
  }

  private getUrl(
    endpoint: string,
    queryParams?: Record<string, any>,
    useAuth = false,
    useMock = false,
  ) {
    const versionMatch = endpoint.match(/^\/(v\d+)(\/.*)$/)
    const version = versionMatch ? versionMatch[1] : this.version
    const path = versionMatch ? versionMatch[2] : endpoint

    const url = (() => {
      if (useMock) {
        return new URL(`${this.baseUrl}/mock/api${path}`)
      }

      if (isServer) {
        return new URL(`${INTERNAL_API_URL}/${version}${path}`)
      }

      return new URL(
        `${this.baseUrl}/${version}${useAuth ? '/use-auth' : ''}${path}`,
      )
    })()

    if (queryParams) {
      Object.entries(queryParams).forEach(([key, value]) => {
        if (
          isNil(value) ||
          value === '' ||
          (isArray(value) && value.length === 0)
        )
          return

        if (isArray(value)) {
          value.forEach(v => {
            url.searchParams.append(key, String(v))
          })
        } else {
          url.searchParams.set(key, String(value))
        }
      })
    }

    return url.toString()
  }

  private async getHeaders(option?: RequestOption, auth: Auth = Auth.NONE) {
    const headers = new Headers({
      'os-type': 'WEB',
      'Content-Type': option?.contentType ?? 'application/json',
      'X-Request-ID': generateXRequestID(),
      ...option?.customHeaders,
    })

    if (isServer) {
      const { headers: nextHeaders } = await import('next/headers')
      const headersInstance = await nextHeaders()

      const userAgent = headersInstance.get('user-agent')
      const xForwardedFor = headersInstance.get('X-Forwarded-For')
      const authorization = headersInstance.get('authorization')

      if (userAgent) {
        headers.set('User-Agent', userAgent)
      }

      if (xForwardedFor) {
        headers.set('X-Forwarded-For', xForwardedFor)
      }

      if (authorization && auth !== Auth.NONE) {
        headers.set('Authorization', authorization)
      }
    }

    return headers
  }

  private getBody(body?: Record<string, any> | null, option?: RequestOption) {
    const contentType = option?.contentType ?? 'application/json'

    if (!body) return null

    if (contentType === ContentType.MULTIPART) {
      const formData = new FormData()

      Object.entries(body).forEach(([key, value]) => {
        formData.append(key, value)
      })

      return formData
    }

    return JSON.stringify(body)
  }

  private async handleResponse<TResult>(
    response: Response,
    method: string,
    requestInit: RequestInit,
  ): Promise<TResult> {
    const json = (await response.json()) as ApiResponse<TResult>
    const { code, data, message } = json

    if (isSuccessResponse(response, json)) {
      return json.data
    }

    // 잘못된 토큰 or 리프레시 토큰 만료
    if (code === AUTH['005'] || code === AUTH['006']) {
      if (!isServer) {
        await signOut()
      }

      // TODO: null로 return하는 경우 에러가 나는 것을 방지하기 위해 확인 후 HttpError를 return하도록 수정
      return null as TResult
    }

    // 액세스 토큰 만료 시
    if (response.status === 401 && code === AUTH['004']) {
      const result = await this.handleRefreshToken<TResult>(
        response,
        method,
        requestInit,
      )

      if (result) {
        return result
      }

      // TODO: null로 return하는 경우 에러가 나는 것을 방지하기 위해 확인 후 HttpError를 return하도록 수정
      return null as TResult
    }

    throw new HttpError({ code, data, message, status: response.status })
  }

  private retryRequests(token: string | null = null) {
    this.failedRequest.forEach(({ resolve }) => {
      resolve(token!)
    })
    this.failedRequest = []
  }

  private async handleRefreshToken<TResult>(
    response: Response,
    method: string,
    requestInit: RequestInit,
  ) {
    if (isServer) {
      return null
    }

    // 이미 토큰 갱신 중이면 대기
    if (this.isRefreshing) {
      const token = await new Promise(resolve => {
        this.failedRequest.push({ resolve })
      })

      const newResponse = await fetch(response.url, {
        ...requestInit,
        headers: {
          ...requestInit.headers,
          Authorization: `Bearer ${token}`,
          'X-Request-ID': generateXRequestID(),
        },
      })

      return this.handleResponse<TResult>(newResponse, method, requestInit)
    }

    // 토큰 재발급
    this.isRefreshing = true

    const res = await fetch(`${API_URL}/api/auth/refresh`, {
      method: 'POST',
      headers: await this.getHeaders(),
      body: JSON.stringify({}),
    })

    if (!res.ok) {
      await signOut()

      return null as TResult
    }

    const newTokens = (await res.json()) as TSignInResponse

    this.isRefreshing = false
    this.retryRequests(newTokens.accessToken)

    // 원래 요청 재시도
    const newResponse = await fetch(response.url, {
      ...requestInit,
      headers: {
        ...requestInit.headers,
        Authorization: `Bearer ${newTokens.accessToken}`,
        'X-Request-ID': generateXRequestID(),
      },
    })

    return this.handleResponse<TResult>(newResponse, method, requestInit)
  }

  public async get<TResult = unknown>({
    endpoint,
    queryParams,
    option,
    auth = Auth.NONE,
  }: {
    endpoint: string
    queryParams?: Record<string, any>
    option?: RequestOption
    auth?: Auth
  }): Promise<TResult> {
    const url = this.getUrl(
      endpoint,
      queryParams,
      auth !== Auth.NONE,
      option?.useMock,
    )
    const headers = await this.getHeaders(option, auth)

    const requestInit = {
      method: 'GET',
      headers,
      ...(option?.tags && { next: { tags: option.tags } }),
      ...(option?.cache && { cache: option.cache }),
      ...(option?.revalidate && { next: { revalidate: option.revalidate } }),
    }
    const response = await fetch(url.toString(), requestInit)

    return this.handleResponse<TResult>(response, 'GET', requestInit)
  }

  public async post<TResult = unknown>({
    endpoint,
    queryParams,
    body,
    option,
    auth = Auth.NONE,
  }: {
    endpoint: string
    queryParams?: Record<string, any>
    body?: Record<string, any> | null
    option?: RequestOption
    auth?: Auth
  }): Promise<TResult> {
    const url = this.getUrl(
      endpoint,
      queryParams,
      auth !== Auth.NONE,
      option?.useMock,
    )
    const headers = await this.getHeaders(option, auth)
    const requestBody = this.getBody(body, option)

    const requestInit = {
      method: 'POST',
      headers,
      body: requestBody,
    }

    const response = await fetch(url.toString(), requestInit)

    return this.handleResponse<TResult>(response, 'POST', requestInit)
  }

  public async put<TResult = unknown>({
    endpoint,
    body,
    option,
    auth = Auth.NONE,
  }: {
    endpoint: string
    body?: Record<string, any> | null
    option?: RequestOption
    auth?: Auth
  }): Promise<TResult> {
    const headers = await this.getHeaders(option, auth)
    const requestBody = this.getBody(body, option)

    const requestInit = {
      method: 'PUT',
      headers,
      body: requestBody,
    }

    const response = await fetch(
      this.getUrl(endpoint, option, auth !== Auth.NONE),
      requestInit,
    )

    return this.handleResponse<TResult>(response, 'PUT', requestInit)
  }

  public async delete<TResult = unknown>({
    endpoint,
    body,
    option,
    auth = Auth.NONE,
  }: {
    endpoint: string
    body?: Record<string, any> | null
    option?: RequestOption
    auth?: Auth
  }): Promise<TResult> {
    const headers = await this.getHeaders(option, auth)
    const requestBody = this.getBody(body, option)

    const requestInit = {
      method: 'DELETE',
      headers,
      body: requestBody,
    }

    const response = await fetch(
      this.getUrl(endpoint, option, auth !== Auth.NONE),
      requestInit,
    )

    return this.handleResponse<TResult>(response, 'DELETE', requestInit)
  }
}

export const apiClient = new ApiClient(API_URL)
